import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const MOBILE_PORTRAIT_QUERY = '(max-width: 749px) and (orientation: portrait)';
const MOBILE_POINTER_QUERY = '(pointer: coarse)';
const MOBILE_HEADER_QUERY = '(max-width: 479px)';
const PREVIEW_PARAMETERS = ['preview_theme_id', 'preview_key', '_fd', 'pb'];

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #isNavigatingToCart = false;
  #mobileHeaderQuery = window.matchMedia(MOBILE_HEADER_QUERY);
  #mobileHeaderGroup;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.#mobileHeaderGroup = this.closest('.nav_button-group');
    this.#syncMobileHeaderCartVisibility();

    if (this.#mobileHeaderQuery.addEventListener) {
      this.#mobileHeaderQuery.addEventListener('change', this.#syncMobileHeaderCartVisibility);
    } else {
      this.#mobileHeaderQuery.addListener(this.#syncMobileHeaderCartVisibility);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    if (this.#mobileHeaderQuery.removeEventListener) {
      this.#mobileHeaderQuery.removeEventListener('change', this.#syncMobileHeaderCartVisibility);
    } else {
      this.#mobileHeaderQuery.removeListener(this.#syncMobileHeaderCartVisibility);
    }

    this.#resetMobileHeaderCartVisibility();
  }

  /**
   * The Webflow mobile stylesheet hides the entire nav button group below
   * 480px, which also hides the only cart trigger. Restore that group on
   * phones while keeping the divider and account icon out of the constrained
   * header so the cart and menu controls remain visible.
   */
  #syncMobileHeaderCartVisibility = () => {
    const group = this.#mobileHeaderGroup;
    if (!(group instanceof HTMLElement)) return;

    const divider = group.querySelector('.nav_divider');
    const account = group.querySelector('.nav_avatar-copy');

    if (this.#mobileHeaderQuery.matches) {
      group.style.setProperty('display', 'flex', 'important');
      group.style.setProperty('gap', '0.25rem', 'important');
      group.style.setProperty('align-items', 'center', 'important');

      if (divider instanceof HTMLElement) divider.style.setProperty('display', 'none', 'important');
      if (account instanceof HTMLElement) account.style.setProperty('display', 'none', 'important');
      return;
    }

    this.#resetMobileHeaderCartVisibility();
  };

  #resetMobileHeaderCartVisibility() {
    const group = this.#mobileHeaderGroup;
    if (!(group instanceof HTMLElement)) return;

    group.style.removeProperty('display');
    group.style.removeProperty('gap');
    group.style.removeProperty('align-items');

    const divider = group.querySelector('.nav_divider');
    const account = group.querySelector('.nav_avatar-copy');

    if (divider instanceof HTMLElement) divider.style.removeProperty('display');
    if (account instanceof HTMLElement) account.style.removeProperty('display');
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  /**
   * Mobile portrait WebViews can open the native dialog backdrop while leaving
   * the drawer outside the visual viewport. Use the dedicated cart page on
   * touch phones in portrait so the cart and checkout always remain reachable.
   *
   * @returns {boolean}
   */
  #shouldUseMobileCartPage() {
    const isMobilePortrait = window.matchMedia(MOBILE_PORTRAIT_QUERY).matches;
    const isTouchDevice = navigator.maxTouchPoints > 0 || window.matchMedia(MOBILE_POINTER_QUERY).matches;

    return isMobilePortrait && isTouchDevice;
  }

  /**
   * Builds the cart URL while retaining Shopify theme-preview parameters.
   *
   * @returns {string}
   */
  #getCartPageUrl() {
    const cartPath = this.dataset.cartUrl || '/cart';
    const cartUrl = new URL(cartPath, window.location.origin);
    const currentUrl = new URL(window.location.href);

    for (const parameter of PREVIEW_PARAMETERS) {
      const value = currentUrl.searchParams.get(parameter);
      if (value !== null) cartUrl.searchParams.set(parameter, value);
    }

    return cartUrl.toString();
  }

  /**
   * Sends portrait-oriented touch devices to the full cart page instead of
   * invoking the native dialog that is failing in the mobile preview WebView.
   *
   * @returns {boolean}
   */
  #openMobileCartPage() {
    if (!this.#shouldUseMobileCartPage()) return false;
    if (this.#isNavigatingToCart) return true;

    this.#isNavigatingToCart = true;
    window.location.assign(this.#getCartPageUrl());
    return true;
  }

  open() {
    if (this.#openMobileCartPage()) return;

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
